import { useCallback, useEffect, useRef } from 'react';
import { ballDiametr, distanceBetweenDots, dotDiametr } from '../initials';

const coeffElementWidth = distanceBetweenDots - dotDiametr;

export const useAnimation = ({
  coeffsArray,
  highlightCoeff,
  ballPositions,
  changeBallPosition,
  animationFactory,
  dotsArr,
  onEndGame,
}) => {
  const highlightedIntervalRef = useRef({});
  const animationStateRef = useRef({});

  const animateCoeffs = useCallback((gameID, newBallPosition) => {
    coeffsArray.forEach((coeff, coeffIndex) => {
      const isXMatch = newBallPosition.x >= coeff.x && newBallPosition.x <= coeff.x + coeffElementWidth - ballDiametr;
      const isYMatch = newBallPosition.y >= coeff.y && newBallPosition.y <= coeff.y + coeffElementWidth - ballDiametr;

      const isCurrentHighlighted = isXMatch && isYMatch;

      const highlightingMeta = highlightedIntervalRef.current[coeffIndex];
      const activatedBySameBall = highlightingMeta?.activeBallGameIDs.includes(gameID) ?? false;

      if (isCurrentHighlighted && !activatedBySameBall) {
        highlightingMeta?.timeoutId && clearTimeout(highlightingMeta.timeoutId);
        highlightedIntervalRef.current[coeffIndex] = {
          timeoutId: setTimeout(() => {
            highlightCoeff(coeffIndex, false);
            highlightedIntervalRef.current[coeffIndex].timeoutId = null;
          }, 125),
          activeBallGameIDs: [...(highlightingMeta?.activeBallGameIDs || []), gameID],
        };

        highlightCoeff(coeffIndex, true);
      }
    });
  }, [coeffsArray, highlightCoeff]);

  const animation = useCallback(gameID => time => {
    const currentAnimationState = animationStateRef.current[gameID];
    const currentBallPosition = ballPositions.find(ballPosition => ballPosition.gameID === gameID);

    const timeFromStart = time - currentAnimationState.startTime;

    if (timeFromStart === 0) {
      requestAnimationFrame(animation(gameID));
      return;
    }

    const newBallPosition = currentAnimationState.animationChain.animate(timeFromStart) ?? currentBallPosition;

    animateCoeffs(gameID, newBallPosition);
    changeBallPosition(gameID, newBallPosition);

    const isEndedAnimation = currentAnimationState.animationChain.getAnimationTime() <= timeFromStart;

    if (isEndedAnimation) {
      onEndGame({
        gameID: currentBallPosition.gameID,
        winning: currentBallPosition.winning,
      });
      return;
    }

    requestAnimationFrame(animation(gameID));
  }, [ballPositions, changeBallPosition, onEndGame, animateCoeffs]);

  useEffect(() => {
    ballPositions.forEach(ballPosition => {
      const animationState = animationStateRef.current[ballPosition.gameID];

      if (animationState) {
        return;
      }

      animationStateRef.current[ballPosition.gameID] = {
        started: false,
      };

      requestAnimationFrame(time => {
        animationStateRef.current[ballPosition.gameID] = {
          started: true,
          startTime: time,
          animationChain: animationFactory.build({
            path: ballPosition.road,
            dotsArr,
          }),
        };

        console.log(animationStateRef.current[ballPosition.gameID].animationChain);

        animation(ballPosition.gameID)(time);
      });
    });
  }, [ballPositions, animation, animationFactory, dotsArr]);
};
